import { GameController } from './app/GameController';
import { isKnownCatalogueId } from './game/content/data';
import { isBuildingUpgradeId, isTowerUpgradeId } from './game/content/upgrades';
import type { CatalogueId } from './game/content/types';
import type { BuildingUpgradeId, TowerUpgradeId } from './game/content/upgrades';
import type { RunMode } from './game/simulation/types';
import { createPhaserGame } from './phaser/createGame';
import './styles.css';

declare global {
  interface Window {
    __CD1_DEBUG__?: {
      snapshot: () => ReturnType<GameController['snapshot']>;
      newRun: (map: 'map-1' | 'map-2' | 'map-3', difficulty: 'easy' | 'normal' | 'hard', mode?: RunMode) => void;
      tick: (count: number) => void;
      startWave: () => void;
      primeVisualQa: () => void;
      unlockForQa: (structure: CatalogueId) => boolean;
      unlockBuildingUpgradeForQa: (upgrade: BuildingUpgradeId) => boolean;
      place: (structure: CatalogueId, x: number, y: number) => boolean;
      upgrade: (x: number, y: number, upgrade: TowerUpgradeId) => boolean;
      upgradeBuilding: (x: number, y: number, upgrade: BuildingUpgradeId) => boolean;
      sell: (x: number, y: number) => boolean;
    };
  }
}

const root = document.querySelector<HTMLElement>('#app');
if (!root) throw new Error('Application root is missing.');

const controller = new GameController();
controller.mount(root);
const host = document.querySelector<HTMLElement>('#phaser-host');
if (!host) throw new Error('Phaser host is missing.');
createPhaserGame(host, controller);

const mapIds = ['map-1', 'map-2', 'map-3'] as const;
const difficultyIds = ['easy', 'normal', 'hard'] as const;
const runModes = ['fixed', 'infinite'] as const;
const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

// Deliberate test hook: it exposes only deterministic actions/snapshots, never renderer-owned state.
window.__CD1_DEBUG__ = {
  snapshot: () => controller.snapshot(),
  newRun: (map, difficulty, mode = 'fixed') => {
    if (!mapIds.includes(map) || !difficultyIds.includes(difficulty) || !runModes.includes(mode)) return;
    controller.chooseMap(map);
    controller.chooseDifficulty(difficulty, mode);
  },
  tick: (count) => {
    if (!isFiniteNumber(count) || count < 0) return;
    controller.debugTick(Math.min(10_000, Math.floor(count)));
  },
  startWave: () => controller.startWave(),
  primeVisualQa: () => controller.debugPrimeVisualQa(),
  unlockForQa: (structure) => isKnownCatalogueId(structure) ? controller.debugUnlockForQa(structure) : false,
  unlockBuildingUpgradeForQa: (upgrade) => isBuildingUpgradeId(upgrade) ? controller.debugUnlockBuildingUpgradeForQa(upgrade) : false,
  place: (structure, x, y) => isKnownCatalogueId(structure) && isFiniteNumber(x) && isFiniteNumber(y)
    ? controller.getSimulation()?.placeStructure(structure, x, y) ?? false
    : false,
  upgrade: (x, y, upgrade) => {
    if (!isFiniteNumber(x) || !isFiniteNumber(y) || !isTowerUpgradeId(upgrade)) return false;
    const simulation = controller.getSimulation();
    const structure = simulation?.getStructureAt(x, y);
    return structure?.kind === 'tower' ? simulation?.upgradeTower(structure.id, upgrade) ?? false : false;
  },
  upgradeBuilding: (x, y, upgrade) => {
    if (!isFiniteNumber(x) || !isFiniteNumber(y) || !isBuildingUpgradeId(upgrade)) return false;
    const simulation = controller.getSimulation();
    const structure = simulation?.getStructureAt(x, y);
    return structure?.kind === 'building' ? simulation?.upgradeBuilding(structure.id, upgrade) ?? false : false;
  },
  sell: (x, y) => isFiniteNumber(x) && isFiniteNumber(y) ? controller.getSimulation()?.sellAt(x, y) ?? false : false,
};
